import { isPlainObject } from 'is-plain-object';

import { assertIsDefined } from '@gitbook/util-ts';

import type { TableGridView } from '../../types';
import type { ViewValidationSpec } from './types';

/** Validation for a grid view */
export const gridViewSpec: ViewValidationSpec<TableGridView> = {
    normalize: (view, table) => {
        let result = view;
        const colDefs = Object.keys(table.definition);

        if (typeof result.hideHeader !== 'boolean') {
            result = {
                ...result,
                hideHeader: false,
            };
        }

        if (typeof result.stickyHeader !== 'boolean') {
            result = {
                ...result,
                stickyHeader: false,
            };
        }

        if (
            typeof result.stickyFirstColumn !== 'boolean' &&
            result.stickyFirstColumn !== undefined
        ) {
            result = {
                ...result,
                stickyFirstColumn: false,
            };
        }

        if (
            typeof result.useNewSizing !== 'boolean' &&
            typeof result.useNewSizing !== 'undefined'
        ) {
            result = {
                ...result,
                useNewSizing: true,
            };
        }

        // At least one visible column
        if (!Array.isArray(result.columns) || result.columns.length === 0) {
            result = {
                ...result,
                columns: colDefs,
            };
        }

        // At this stage, we should have columns
        assertIsDefined(result.columns);

        // Column orders should have unique columns
        const uniqueSet = new Set(result.columns);
        const colDefsSet = new Set(colDefs);
        for (const key of uniqueSet) {
            if (!colDefsSet.has(key)) {
                uniqueSet.delete(key);
            }
        }
        if (uniqueSet.size !== result.columns.length) {
            result = {
                ...result,
                columns: [...uniqueSet],
            };
        }

        // Widths should only list visible columns
        const initialColumnWidths =
            result.columnWidths && isPlainObject(result.columnWidths) ? result.columnWidths : {};

        let columnWidths: Record<string, number> = initialColumnWidths;
        for (const key of Object.keys(initialColumnWidths)) {
            if (!uniqueSet.has(key) || typeof columnWidths[key] !== 'number') {
                columnWidths = { ...columnWidths };
                delete columnWidths[key];
            }
        }

        if (columnWidths !== result.columnWidths) {
            result = {
                ...result,
                columnWidths,
            };
        }

        return result as TableGridView;
    },
};
